import { useEffect } from 'react';

const CODE = 'JUNE';

// Domains where we silently append the promo code so the discount
// auto-applies at checkout. NO popup, NO modal — pure URL rewrite.
const CHECKOUT_DOMAINS = [
  'fanbasis.com',
  'freedombux.com',
  'thewealthcollection.com',
  'mycreditmonitor.io',
  'iqdata.io',
];

const PROMO_PARAMS = ['discount_code', 'coupon', 'promo', 'promo_code', 'code', 'discount'];

const isCheckoutHref = (href) => {
  if (!href || !/^https?:\/\//i.test(href)) return false;
  try {
    const u = new URL(href);
    return CHECKOUT_DOMAINS.some((d) => u.hostname.endsWith(d));
  } catch {
    return false;
  }
};

const rewriteWithPromo = (href) => {
  try {
    const u = new URL(href);
    let changed = false;
    PROMO_PARAMS.forEach((p) => {
      if (!u.searchParams.has(p)) {
        u.searchParams.set(p, CODE);
        changed = true;
      }
    });
    return changed ? u.toString() : href;
  } catch {
    return href;
  }
};

/**
 * PromoCodeAppender
 * ------------------------------------------------------------------
 * Mounted once at the app root. Delegates a single capture-phase click
 * listener on `document`. When the user clicks a link pointing to any
 * known checkout domain, we silently rewrite the anchor's href so that
 * the JUNE 50% off code is appended as a query param (under every common
 * platform parameter name) — this makes the discount auto-apply on the
 * merchant's checkout page. No UI, no interruption.
 *
 * Design notes:
 *  - We mutate `link.href` in place instead of calling preventDefault +
 *    window.open, so target="_blank" / user-agent hotkey behaviour (open
 *    in new tab, cmd+click, etc.) all remain native.
 *  - Idempotent: if the code is already on the URL we skip.
 *  - Never touches non-checkout links (calendly, internal routes, etc.).
 */
const PromoCodeAppender = () => {
  useEffect(() => {
    const onClick = (e) => {
      const link = e.target?.closest?.('a[href]');
      if (!link) return;
      const href = link.getAttribute('href');
      if (!isCheckoutHref(href)) return;
      const rewritten = rewriteWithPromo(href);
      if (rewritten !== href) link.setAttribute('href', rewritten);
    };
    // capture phase — run before React's synthetic click / any target=_blank
    document.addEventListener('click', onClick, true);
    // also handle keyboard activation (Enter on focused link)
    document.addEventListener('auxclick', onClick, true);
    return () => {
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('auxclick', onClick, true);
    };
  }, []);

  return null;
};

export default PromoCodeAppender;
